import { context, ticks, TimeAxis, type AxisCalendar } from "../editor/time-axis.js";

// A timeline drawn on an axis (docs/tapestry-design.md §3.5, 2b part 5), beside the list: the same timeline from the
// server (only what the person may see), zoomed and moved in the browser. Events are dots (bars when they take a while),
// other parts' times small marks, Now a line; rows stack so labels don't overlap. Nothing changes by looking.

type Item = {
  kind: string;
  id: string;
  title: string;
  shown: string | null;
  start: number | null;
  end: number | null;
  status: string;
  notTaken?: boolean;
  pastDue?: boolean;
  around?: boolean;
  overdue?: boolean;
  done?: boolean;
  what?: string;
  url: string;
  page: string | null;
};
type Data = { calendar: AxisCalendar; items: Item[]; undated: Item[]; nows: Array<{ key: number; shown: string; page: string }> };

const ROW = 24;
const AXIS = 28;
const MIN_SPAN = 30 * 60;
const MAX_SPAN = 1e12;
const STORE = "p9.timelineView";

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { class?: string } = {}, ...children: Array<Node | string | null>): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const { class: className, ...rest } = props;
  if (className) el.className = className;
  Object.assign(el, rest);
  for (const child of children) if (child !== null) el.append(child);
  return el;
}

export class P9TimelineChart extends HTMLElement {
  #data: Data | null = null;
  #axis = new TimeAxis(null);
  #start = 0;
  #end = 1;
  #view!: HTMLDivElement;
  #context!: HTMLSpanElement;
  #rows = new Map<Item, number>();
  #rowsFor = 0;
  #loaded = false;

  connectedCallback(): void {
    // The List / Chart buttons of the box this chart is in; the choice is remembered on this device.
    const box = this.closest(".tapestry-box");
    const buttons = [...(box?.querySelectorAll<HTMLButtonElement>("[data-timeline-show]") ?? [])];
    const list = box?.querySelector<HTMLElement>(".tp-timeline-list");
    const show = (which: string) => {
      for (const b of buttons) b.setAttribute("aria-pressed", String(b.dataset.timelineShow === which));
      if (list) list.hidden = which === "chart";
      this.hidden = which !== "chart";
      if (which === "chart") void this.#activate();
    };
    for (const b of buttons) b.addEventListener("click", () => {
      show(b.dataset.timelineShow!);
      try { localStorage.setItem(STORE, b.dataset.timelineShow!); } catch { /* no storage: just this time */ }
    });
    let remembered = "list";
    try { remembered = localStorage.getItem(STORE) ?? "list"; } catch { /* no storage */ }
    show(remembered === "chart" ? "chart" : "list");
  }

  async #activate(): Promise<void> {
    if (this.#loaded) return void this.#draw();
    this.#loaded = true;
    this.replaceChildren(h("p", { class: "muted small" }, "Loading the chart…"));
    const query = new URLSearchParams({ handler: "TimelineData", on: this.dataset.on ?? "", under: this.dataset.under === "true" ? "true" : "false" });
    if (this.dataset.calendar) query.set("calendar", this.dataset.calendar);
    try {
      const response = await fetch(`${document.body.dataset.base ?? "/"}tapestry/fragment?${query}`);
      if (!response.ok) throw new Error();
      this.#data = (await response.json()) as Data;
    } catch {
      this.replaceChildren(h("p", { class: "muted small" }, "The chart couldn't load; the list shows the same timeline."));
      return;
    }
    this.#axis = new TimeAxis(this.#data.calendar);
    this.#build();
    this.#fit(false);
  }

  #build(): void {
    const zoomIn = h("button", { type: "button", class: "button secondary small", textContent: "+", title: "Zoom in (or Ctrl + wheel, or pinch)" });
    const zoomOut = h("button", { type: "button", class: "button secondary small", textContent: "−", title: "Zoom out" });
    const fit = h("button", { type: "button", class: "button secondary small", textContent: "All", title: "Show everything, from the first to the last" });
    zoomIn.addEventListener("click", () => this.#zoom(0.5));
    zoomOut.addEventListener("click", () => this.#zoom(2));
    fit.addEventListener("click", () => this.#fit(true));
    this.#context = h("span", { class: "tc-context" });
    this.#view = h("div", { class: "tc-view", tabIndex: 0 });
    this.#view.setAttribute("role", "group");
    this.#view.setAttribute("aria-label", "Timeline chart: drag to move, Ctrl + wheel or pinch to zoom, arrow keys and + / − too");
    const parts: Array<HTMLElement | null> = [
      h("div", { class: "tc-tools" }, zoomOut, zoomIn, fit, this.#context,
        h("span", { class: "muted small tc-hint", textContent: "Drag to move · Ctrl + wheel or pinch to zoom" })),
      this.#view,
    ];
    if (this.#data!.undated.length) {
      const list = h("ul", { class: "tc-undated" });
      for (const item of this.#data!.undated) list.append(h("li", {}, h("a", { href: item.url, textContent: item.title }), item.shown ? ` (${item.shown})` : ""));
      parts.push(h("p", { class: "tp-section", textContent: "Undated" }), list);
    }
    this.replaceChildren(...parts.filter((p): p is HTMLElement => p !== null));
    this.#interact();
    new ResizeObserver(() => this.#draw()).observe(this.#view);
  }

  // Everything in view (all), or where most things are: far-off outliers (a city founded centuries ago) left out at
  // first, Now always in. A little room on either side.
  #fit(all: boolean): void {
    const data = this.#data!;
    const times = data.items.flatMap((i) => [i.start!, i.end ?? i.start!]).sort((a, b) => a - b);
    const nows = data.nows.map((n) => n.key);
    if (times.length + nows.length === 0) {
      this.#view.replaceChildren(h("p", { class: "muted small tc-empty" }, "Nothing with a date on this timeline yet."));
      return;
    }
    // Outliers: further than three times the spread of the middle half from it.
    const [q1, q3] = [times[Math.floor((times.length - 1) / 4)]!, times[Math.ceil(((times.length - 1) * 3) / 4)]!];
    const reach = Math.max(3 * (q3 - q1), 30 * 86400);
    const kept = all || times.length < 5 ? times : times.filter((t) => t >= q1 - reach && t <= q3 + reach);
    let [min, max] = [Math.min(...kept, ...nows), Math.max(...kept, ...nows)];
    if (max - min < 86400) [min, max] = [min - 15 * 86400, max + 15 * 86400];
    const pad = (max - min) * 0.06;
    [this.#start, this.#end] = [min - pad, max + pad];
    this.#draw();
  }

  #zoom(factor: number, at = 0.5): void {
    const span = this.#end - this.#start;
    const next = Math.min(MAX_SPAN, Math.max(MIN_SPAN, span * factor));
    const pivot = this.#start + span * at;
    [this.#start, this.#end] = [pivot - next * at, pivot + next * (1 - at)];
    this.#draw();
  }

  #pan(pixels: number): void {
    const shift = (pixels * (this.#end - this.#start)) / Math.max(1, this.#view.clientWidth);
    [this.#start, this.#end] = [this.#start - shift, this.#end - shift];
    this.#draw();
  }

  #interact(): void {
    const view = this.#view;
    const pointers = new Map<number, number>();
    let moved = 0;
    let pinch = 0;
    view.addEventListener("pointerdown", (event) => {
      pointers.set(event.pointerId, event.clientX);
      moved = 0;
      pinch = 0;
      view.setPointerCapture(event.pointerId);
    });
    view.addEventListener("pointermove", (event) => {
      if (!pointers.has(event.pointerId)) return;
      const last = pointers.get(event.pointerId)!;
      pointers.set(event.pointerId, event.clientX);
      if (pointers.size === 2) {
        // Two fingers: zoom around the middle of them.
        const [a, b] = [...pointers.values()];
        const distance = Math.abs(a! - b!);
        if (pinch && distance) {
          const rect = view.getBoundingClientRect();
          this.#zoom(pinch / distance, ((a! + b!) / 2 - rect.left) / rect.width);
        }
        pinch = distance;
        moved += 10;
        return;
      }
      moved += Math.abs(event.clientX - last);
      this.#pan(event.clientX - last);
    });
    const up = (event: PointerEvent) => pointers.delete(event.pointerId);
    view.addEventListener("pointerup", up);
    view.addEventListener("pointercancel", up);
    // A drag isn't a click on what's under it.
    view.addEventListener("click", (event) => {
      if (moved > 4) event.preventDefault();
    }, true);
    view.addEventListener("wheel", (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = view.getBoundingClientRect();
      this.#zoom(event.deltaY > 0 ? 1.25 : 0.8, (event.clientX - rect.left) / rect.width);
    }, { passive: false });
    view.addEventListener("keydown", (event) => {
      const width = view.clientWidth;
      if (event.key === "ArrowLeft") this.#pan(width / 8);
      else if (event.key === "ArrowRight") this.#pan(-width / 8);
      else if (event.key === "+" || event.key === "=") this.#zoom(0.5);
      else if (event.key === "-") this.#zoom(2);
      else return;
      event.preventDefault();
    });
  }

  // Rows at this zoom level, for every item (so moving sideways never reshuffles them): each goes in the first row
  // where its dot, bar and label fit after what's already there.
  #layout(secPerPixel: number): void {
    if (this.#rowsFor === secPerPixel) return;
    this.#rowsFor = secPerPixel;
    this.#rows.clear();
    const ends: number[] = [];
    const items = [...this.#data!.items].sort((a, b) => a.start! - b.start!);
    for (const item of items) {
      const x1 = item.start! / secPerPixel;
      const x2 = (item.end ?? item.start!) / secPerPixel;
      // The label follows the dot, or the end of the bar.
      const right = (item.end !== null ? x2 : x1) + Math.min(280, item.title.length * 7 + 22) + 10;
      let row = ends.findIndex((end) => end < x1 - 6);
      if (row < 0) row = ends.push(0) - 1;
      ends[row] = right;
      this.#rows.set(item, row);
    }
  }

  #draw(): void {
    if (!this.#data || !this.#view || this.hidden) return;
    const width = this.#view.clientWidth;
    if (width <= 0) return;
    const span = this.#end - this.#start;
    const secPerPixel = span / width;
    // Rows depend on the zoom only (rounded, so small float differences don't reshuffle them).
    this.#layout(Number(secPerPixel.toPrecision(6)));
    const x = (t: number) => (t - this.#start) / secPerPixel;
    const rows = Math.max(1, ...[...this.#rows.values()].map((r) => r + 1));
    // Room below the rows for the Now labels.
    const height = AXIS + rows * ROW + 30;
    const parts: HTMLElement[] = [];
    for (const tick of ticks(this.#axis, this.#start, this.#end, width)) {
      const left = x(tick.at);
      parts.push(h("span", { class: `tc-grid${tick.major ? " tc-major" : ""}` }), h("span", { class: `tc-tick${tick.major ? " tc-major" : ""}`, textContent: tick.label }));
      parts[parts.length - 2]!.style.left = parts[parts.length - 1]!.style.left = `${left}px`;
    }
    for (const now of this.#data.nows) {
      const left = x(now.key);
      if (left < -100 || left > width + 100) continue;
      const line = h("span", { class: "tc-now", title: `Now: ${now.shown} (${now.page})` }, h("span", { class: "tc-now-label", textContent: `Now ${now.shown}` }));
      line.style.left = `${left}px`;
      parts.push(line);
    }
    for (const item of this.#data.items) {
      const x1 = x(item.start!);
      const x2 = x(item.end ?? item.start!);
      if (x2 < -300 || x1 > width + 20) continue;
      const classes = ["tc-item", `tc-${item.kind}`, `st-${item.status}`];
      if (item.notTaken) classes.push("tc-not-taken");
      if (item.pastDue || item.overdue) classes.push("tc-late");
      if (item.around) classes.push("tc-around");
      if (item.done) classes.push("tc-done");
      const tip = [item.shown, item.title, item.page ? `on ${item.page}` : null, item.notTaken ? "not taken" : null].filter(Boolean).join(" · ");
      const link = h("a", { class: classes.join(" "), href: item.url, title: tip },
        item.end !== null && x2 - x1 > 3 ? h("span", { class: "tc-bar" }) : h("span", { class: "tc-dot" }),
        h("span", { class: "tc-label", textContent: item.title }));
      link.style.left = `${x1}px`;
      link.style.top = `${AXIS + this.#rows.get(item)! * ROW}px`;
      const bar = link.querySelector<HTMLElement>(".tc-bar");
      if (bar) bar.style.width = `${x2 - x1}px`;
      parts.push(link);
    }
    this.#view.style.height = `${height}px`;
    this.#view.replaceChildren(...parts);
    this.#context.textContent = context(this.#axis, this.#start, this.#end);
  }
}

customElements.define("p9-timeline-chart", P9TimelineChart);
